import { useContext, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { Button } from '@repo/ui'
import { ArrowUpRight, ChevronRight } from 'lucide-react'

import { DrawerPopup } from 'components/UI/DrawerPopup'
import { CompanyContext } from 'contexts/companyContext'
import { useGetCompanyBySubdomain, useGetCompanyStyles } from 'hooks/api'
import { border, font, typeface } from 'settings/tokens'
import { VERA_INK, VERA_INK_MUTED, VERA_SURFACE } from 'settings/veraMark'
import { demoBrandSlug, extractSubdomain } from 'utils/subdomain'

/**
 * VRG-309 — the provenance disclosure on an acquisition demo certificate.
 *
 * ── STILL A BANNER, BUT ONE LINE AND A CHEVRON (user directive, 2026-08-31) ──
 *
 * This shipped as a full-bleed near-black slab carrying a headline, a
 * paragraph and a white CTA button, wedged between the hero photograph and the
 * product's name. Verdict on seeing it: *"this looks horrible. Fix colouring
 * … and it should be independent colouring to the brand studio setting."*
 *
 * The slab was wrong on its own terms, not merely to taste. It was the visually
 * loudest element on a page whose entire pitch is *this is what YOUR product
 * certificate would look like* — so the one block that is emphatically not part
 * of that product dominated the demonstration of it. And it spent a full
 * paragraph plus a button on a disclosure that has to be READ ONCE; permanent
 * chrome should carry the claim, not the essay.
 *
 * An inline chip was built first and then rejected, so do not re-propose it:
 * *"demo badge should be a banner right below the images — all white with black
 * text, that has a chevron to open the drawer with the info. Not a badge."*
 * The chip read as one more pill in a page that already has one (`AuthenticChip`
 * sits on the photograph immediately above), and two pills in one visual
 * language saying unrelated things is a confusion this page has removed once
 * before — see the `TransferCompletionChip` note in `CertificatePage`.
 *
 * The chip was proposed a SECOND time (2026-11-03, "a badge like AUTHENTIC but on
 * the right") and rejected again, on a stronger ground than the first: a demo
 * certificate with a real chip bound to it (VRG-388 "Add tag") SCANS VALID, so
 * `AuthenticChip` reads "Authentic" on it. A DEMO pill beside that would have the
 * page assert verified and not-real at once, in one visual language, on one
 * photograph. Not clutter — a contradiction.
 *
 * ── VRG-825: IT NO LONGER SITS BELOW THE IMAGES ────────────────────────────
 *
 * The directive quoted above said "right below the images", and that is where it
 * stood until the bar moved ABOVE `CertificateBrandHeader` — i.e. above the brand's
 * own logo bar, at the very top of the page. Read that quote as settling
 * BAR-NOT-CHIP, which still holds; its placement clause is superseded.
 *
 * The reason is specific to how a demo is built: provisioning SCRAPES the prospect's
 * site, logo included, so the brand mark is demo data too. A disclosure under it
 * implicitly exempts it. Above it, the claim covers the branding as well, which is
 * what is actually true. The call site carries the full argument, including what the
 * move gives up (the previous position was argued for, not stumbled into).
 *
 * So: a FULL-BLEED BAR on its own row, one line of text, a trailing chevron,
 * the whole bar tappable. The chevron is the signal, not the target — a control
 * you must hit precisely is worse than a bar you can tap anywhere. What it
 * opens is the same `DrawerPopup` sheet `AuthenticityDrawer` uses (VRG-240), so
 * the *interaction* still borrows the app's existing "tap the thing that states
 * a fact, get the explanation" vocabulary even though the silhouette does not.
 *
 * ── What did NOT change, and must not ───────────────────────────────────────
 *
 * The disclosure's CLAIMS are unchanged and the sentences below are the
 * previously-reviewed ones, redistributed rather than rewritten. Specifically:
 *
 *  • The bar is ALWAYS VISIBLE and carries BOTH load-bearing claims — that this
 *    is a demo, and that the brand did not issue it. Moving the second claim
 *    into the drawer would have been the cheap way to make the line short, and
 *    it is exactly the failure VRG-305 Gap 1 names: "Demo" implies unofficial,
 *    it does not say UNAFFILIATED/UNAUTHORISED, and that distinction is the
 *    whole of the epic's legal exposure. A claim behind a tap is a claim most
 *    readers never see. Shorten anything here except that.
 *  • NON-DISMISSIBLE (VRG-313 AC8). The DRAWER closes; the BAR does not. There
 *    is deliberately no `onClose`, no `dismissible` prop and no settings lookup
 *    — a demo page without this disclosure is the artifact the whole epic exists
 *    to prevent, so the component offers no way to produce one.
 *  • The bar and its drawer live in ONE component and the bar owns the drawer's
 *    state. The call site cannot render the bar and forget the explanation, or
 *    vice versa. (This is why the drawer is not a sibling file next to
 *    `AuthenticityDrawer`: separability is the risk being designed out.)
 *
 * ── Why the page below is the REAL certificate, not a bespoke layout ────────
 *
 * VRG-309 AC1 as authored asked for "a distinct layout from real certificates".
 * That was overridden (user, 2026-08-20) and the override is the better call: the
 * entire value of the demo is showing a prospect what THEIR certificate would
 * look like, so a bespoke demo layout would be demonstrating something we do not
 * ship — and it would drift out of sync the moment the real page changed, making
 * the demo progressively less true.
 *
 * ── Why the copy branches (VRG-388) ─────────────────────────────────────────
 *
 * The disclosure originally had one audience: a cold prospect who has never heard
 * of Vera. Under the takeover model that same certificate can outlive the pitch —
 * the brand signs up, is invited into the SAME company, moves onto their own
 * domain, and may deliberately keep the demo. At that point "Vera is not
 * affiliated with them" is no longer a careful disclaimer, it is a false
 * statement about a paying customer, published on their own site.
 *
 * So the disclosure keeps the half that stays true regardless (this record is
 * sample data the brand did not issue) and swaps the half that does not. See
 * `audience`.
 *
 * ── Deliberate inversion of this app's state-page doctrine ──────────────────
 *
 * Every other special-state page here (CertificateNotActive, TagReplaced,
 * CompanySuspended) carries NO Vera identity at all, on the documented grounds
 * that "the visitor believes they are dealing with the brand, and the brand is
 * who can actually look the item up". Read those files' headers before touching
 * this one, because this component breaks that rule ON PURPOSE: here the visitor
 * IS the brand, they have never heard of Vera, and telling them who made this and
 * what we do is the entire point. Do not "fix" this for consistency.
 */
export interface DemoBannerProps {
  /**
   * Display name from the API when available. Falls back to the subdomain slug,
   * because the API lookup that supplies this is best-effort and must never be
   * able to suppress the disclosure — see utils/subdomain.ts `isDemoHost`.
   */
  brandName?: string
  /** Where "See what Vera does" goes. */
  ctaUrl?: string
  /**
   * VRG-388 — WHO is reading this, which decides what the disclosure has to say.
   *
   * `'prospect'` (default) is cold outbound: the brand has no relationship with us, this was
   * built from their public site without asking, and the recipient may be seeing Vera's name
   * for the first time. Non-affiliation is the load-bearing claim.
   *
   * `'customer'` is AFTER TAKEOVER: the brand signed up, the demo lives on their own account
   * and their own domain, and they may deliberately be keeping it. Here the cold-outbound
   * wording is not merely off-tone, it is FALSE — we ARE affiliated with them, they are a
   * customer, and telling their visitors otherwise is worse than saying nothing. What still
   * needs saying is the part that stays true: this particular record is sample data, not a
   * real item, and its images and description were not authored by them.
   *
   * Note that both states ARE demos. They differ only in whether Vera has a relationship with
   * the brand, so that is the only sentence that changes — the "this is a demo" half is
   * constant, because it is the half that protects a visitor either way.
   *
   * ⚠️ It changes what the disclosure SAYS, never whether it renders. There is no value of it
   * that produces no banner.
   */
  audience?: 'prospect' | 'customer'
  /**
   * Where the bar PINS, in px from the top of the viewport. The bar is
   * `position: sticky`, so this is what it sticks to.
   *
   * ⚠️ THE CALL SITE MUST PASS THE HEIGHT OF WHATEVER ELSE IS PINNED ABOVE IT.
   * On `CertificatePage` that is `NavigationHeader` — a sticky glass bar at
   * `top: 0`, `NAV_HEADER_HEIGHT` tall, `z-index: 1100`. It renders only for the
   * owner and the incoming-transfer recipient, so the correct offset is
   * conditional and cannot be decided here. Pinning at 0 underneath it would put
   * this bar BEHIND the glass (1100 beats anything a page element should claim),
   * i.e. the disclosure would silently disappear for exactly the viewer who has
   * a header.
   *
   * Default 0 — pin to the very top — which is right wherever nothing else is
   * pinned, including the cold-prospect view this component was built for.
   */
  stickyTop?: number
}

/**
 * The marketing site ROOT, deliberately — `https://vera.inc/demo` was the previous
 * default and it **404s** (measured 2026-08-24; the root returns 200). This is the one
 * conversion control on a demo certificate, at the end of the only chain of intent the
 * whole play produces — scrape, build, encode a chip, mail a card, prospect taps, reads
 * the disclosure, clicks. Landing that click on a 404 is the worst available outcome,
 * and a generic-but-live homepage beats a targeted-but-dead page every time.
 *
 * If marketing later builds a real `/demo` — a page that knows the visitor arrived from
 * a demo certificate and can convert on that — point this back at it. `ctaUrl` is a prop
 * precisely so a call site can override without touching this component.
 */
const DEFAULT_CTA_URL = 'https://vera.inc'

/**
 * WHITE BAR, BLACK TEXT — fixed hexes, stated literally by the user, and the
 * literalness is the point rather than an accident of taste.
 *
 * ⚠️ DO NOT REPLACE THESE WITH `theme.palette.*`, INCLUDING `common.white` AND
 * `text.primary`. Every colour hook this app exposes is, directly or by
 * derivation, the TENANT'S: `text.primary` IS their Studio `text_color`,
 * `background.default` their `bg_color`, `primary.main` their `primary_color`,
 * and `brand[*]` is derived from `primary_color` by `settings/mui/brandRamp.ts`
 * (see `settings/tokens.ts`, which documents that routing as the white-label
 * promise). The previous version read `theme.palette.grey[900]` and called
 * itself "deliberately NOT the brand's primary_color" — but greys are a palette
 * a theme layer may legitimately restate, so that was a convention holding it in
 * place, not a guarantee.
 *
 * This element is the one part of the page that is VERA speaking rather than the
 * brand, and it is a legal disclosure. Two consequences follow and both need
 * fixed values:
 *
 *  1. It must not LOOK brand-authored. A bar tinted from the tenant's palette
 *     reads as part of their design, which is precisely the impression the
 *     disclosure exists to prevent.
 *  2. It must stay LEGIBLE whatever the tenant configured. Measured live: under
 *     a Studio theme with `text_color: '#F2E9D8'`, the neighbouring
 *     `AuthenticChip` — which reads `color: 'text.primary'` — rendered cream
 *     text on its own white pill, i.e. a status indicator that had become
 *     invisible, with every automated check still green. Fixed ink on an opaque
 *     fill makes contrast a property of THIS component rather than of their
 *     settings. Black on white is 21:1, the maximum available.
 *
 * ⚠️ WHITE IN BOTH MODES, deliberately, and this is a KNOWN cost rather than an
 * oversight. This app has no `palette.mode` toggle and nothing sets the `.dark`
 * class in `index.css` — its "dark mode" is a TENANT choosing a dark Studio
 * `bg_color`, which is a real and supported configuration. On such a page this
 * bar is a bright white band, which is loud. It is white anyway, on instruction,
 * and because loud is the correct failure direction for a legal disclosure: the
 * mode where it is easy to miss is the dangerous one. If it is ever softened,
 * soften it with a token here, never by reaching back into the tenant's palette.
 *
 * The hairlines are `border.subtle` — already a Vera-owned token
 * (`settings/tokens.ts` calls border greys "the one genuinely Vera-owned colour
 * set"), reused rather than restated. They are STRUCTURE, not a departure from
 * "all white": the page surface defaults to `#F7F7F8` and a brand may set it to
 * pure white, in which case an unbordered white bar has no edge at all.
 */
// VRG-827: VERA_SURFACE / VERA_INK / VERA_INK_MUTED moved to `settings/veraMark.ts` and are
// imported above. `AuthenticChip` reached the same three values independently and then
// half-applied them, which is what made one source worth having (working-conventions §36).
/**
 * VERA'S TYPEFACE, pinned for exactly the reason the colours above are.
 *
 * Scraping a prospect's site now applies their palette AND their fonts, so this
 * bar inherited the tenant's body face — measured `Playfair Display` on one demo
 * and `Lato` on another. That is the same failure as a brand-tinted surface, one
 * layer down: a disclaimer set in the brand's own typeface reads as
 * brand-authored, which is precisely the inference the bar exists to prevent
 * (VRG-305 Gap 1 — "Demo" implies unofficial, it does not say UNAFFILIATED, and
 * that distinction is the epic's whole legal exposure). Colour independence
 * without font independence is half a fix.
 *
 * `typeface.sans` is the app's OWN stack, already resolved on every page, so this
 * costs no font request and introduces no new family. It is system-first, so on
 * the phone this page is reached from it renders as SF Pro — which is also the
 * most "not the brand's" a face can look on that device, because it is the OS's.
 *
 * ⚠️ IT HAS TO BE RESTATED ON EACH TEXT NODE, not set once on the bar and
 * inherited. MUI's `Typography` and `Button` write `font-family` explicitly from
 * `theme.typography` (which `settings/mui/typography.ts` sets to the tenant's
 * font), so an inherited value on an ancestor loses to their own declaration.
 * Every place this constant appears below is a place that would otherwise be the
 * brand's face.
 */
const VERA_TYPEFACE = typeface.sans
/**
 * Secondary ink, used ONLY inside the drawer to demote its second paragraph.
 * The BAR itself is pure black on pure white with no second tone — "all white
 * with black text" was the instruction and a two-tone bar is not that.
 */
const VERA_HAIRLINE = border.subtle
/** Focus ring — Vera's ink at low alpha, so it is visible on any tenant surface. */
const VERA_FOCUS_RING = 'rgba(0, 0, 0, 0.18)'

/**
 * THE ALWAYS-VISIBLE CLAIM. Both halves are load-bearing and neither may be
 * trimmed for tone or for width — see the header's "What did NOT change".
 *
 * "Demo" alone says unofficial; it does not say the brand had no hand in this.
 * "Not issued by <Brand>" is the half that does, and it is the half with the
 * legal weight, so it stays on the bar rather than moving into the drawer.
 *
 * ── NAMES THE BRAND (user directive, 2026-08-31) ────────────────────────────
 *
 * *"this is a demo certificate -> not issued by [company name]"*. This was
 * "not issued by this brand", and naming them makes the SAME claim STRONGER
 * rather than merely warmer. "This brand" is a deictic — it points at whatever
 * the reader takes the page to be about, and the page is wearing the brand's
 * scraped colours, fonts, logo and product photography, so the phrase resolves
 * to something the reader has to infer. "Not issued by Allbirds" cannot be
 * misread. VRG-305 Gap 1 makes the non-affiliation claim non-optional and
 * always-visible precisely because it is the epic's whole legal exposure; a
 * named subject is the most concrete form that claim can take.
 *
 * STILL ONE STRING FOR BOTH AUDIENCES, and naming the brand does not change
 * that. Post-takeover, on the brand's own domain, "this is a demo certificate,
 * not issued by Allbirds" remains true word for word: the record IS a demo, and
 * Allbirds did NOT issue it — Vera created it from their public site. If
 * anything the named form is MORE useful there, because the visitor is on
 * Allbirds' own site and "this brand" is at its most ambiguous exactly then.
 * What must not survive takeover is the NON-AFFILIATION claim and the pitch,
 * and neither is here; both live in the drawer, which does branch.
 *
 * ── NAMES VERA, BUT ONLY TO THE PROSPECT (user directive, 2026-08-31) ───────
 *
 * *"update to include our brand: 'This is a demo Vera certificate, not issued
 * by ___'"*. Naming the author is a genuine strengthening: "not issued by
 * Allbirds" says who DIDN'T make it and leaves who DID unstated, which on a
 * page wearing Allbirds' own colours, fonts and photography invites the reader
 * to fill the gap with Allbirds. "A demo Vera certificate" closes it.
 *
 * ⚠️ SO WHY DOES THE CUSTOMER STRING STILL OMIT VERA? Because WHITE LABEL is a
 * paid enterprise entitlement (VERA-644 / TD-066), and under it Vera's name
 * "must not render AT ALL". `PoweredByVera` enforces that by REFUSING TO RENDER
 * ITSELF rather than trusting call sites — a decision taken after the
 * authenticity drawer became a second surface and leaked the attribution while
 * the footer correctly hid it. A "Vera" welded into a SENTENCE is that same leak
 * a third time, and worse: a component can refuse to render, a substring cannot.
 *
 * The prospect case cannot hit that entitlement — a cold prospect is on a
 * `-demo` host and is not a paying customer, let alone a white-label one. The
 * customer case is exactly where the entitlement lives, and it is also the case
 * the earlier "<Brand> × Vera" headline was dropped for: on a customer's own
 * live domain, Vera's name in permanent chrome reads as a partnership
 * announcement to their visitors.
 *
 * So this branches on audience where it previously did not, and the split is
 * the entitlement's, not tone's. If the branded form is ever wanted
 * post-takeover, gate it on the white-label flag itself rather than widening
 * this — audience is a proxy for the entitlement, and a proxy is what leaks.
 *
 * ⚠️ THE BRAND-LESS FORM IS NOT A DEGRADED FALLBACK, it is the same claim with
 * an unnamed subject, and it must stay. `brandLabel` is genuinely absent
 * post-takeover — there is no `-demo` slug off a demo host and the company
 * lookup need not still carry demo metadata — so this function is called with
 * `null` on real pages, not only in theory.
 *
 * A comma, not an em-dash (§15).
 */
const bannerLabel = (
  brandLabel: string | null,
  audience: 'prospect' | 'customer',
): string => {
  // "a demo Vera certificate" for the prospect; plain "a demo certificate" for a
  // brand that has taken the demo over. See the white-label note above — this
  // split exists to honour a paid entitlement, not for tone.
  const noun =
    audience === 'prospect' ? 'a demo Vera certificate' : 'a demo certificate'

  return brandLabel
    ? `This is ${noun}, not issued by ${brandLabel}`
    : `This is ${noun}, not issued by this brand`
}

export const DemoBanner = ({
  brandName,
  ctaUrl = DEFAULT_CTA_URL,
  audience = 'prospect',
  stickyTop = 0,
}: DemoBannerProps) => {
  const [open, setOpen] = useState(false)

  // Both of these read react-query entries `App.tsx` has ALREADY populated for
  // the theme, so they are cache hits rather than new requests. Their failure is
  // deliberately unhandled: if either errors or is still loading, the fallbacks
  // below carry the copy and the disclosure still renders.
  const { data } = useGetCompanyBySubdomain(extractSubdomain())
  // The second source, added when the bar started NAMING the brand. The
  // subdomain query above is DISABLED whenever there is no subdomain — which is
  // every local dev page and, more importantly, every page after takeover, when
  // the brand is on their own domain. This one is keyed on the company id the
  // page already resolved, so it is available wherever the certificate is.
  const { companyId } = useContext(CompanyContext)
  const { data: company } = useGetCompanyStyles({ companyId: companyId || undefined })

  const slug = demoBrandSlug()

  /*
    Fall back through: prop -> demo enrichment -> the company's own name -> the
    subdomain slug -> an unnamed but otherwise identical sentence.

    ⚠️ ONLY THE SLUG IS TITLE-CASED. An API-supplied display name is the brand's
    OWN spelling of itself and must be printed verbatim: title-casing turns
    "lululemon" into "Lululemon" and "eBay" into "EBay", i.e. it misspells the
    one word on the bar whose accuracy the whole disclosure now rests on. The
    slug is a different case entirely — it is lowercase-by-construction out of
    the subdomain ("ysl-demo" -> "ysl"), so it has no capitalisation of its own
    to preserve and title-casing is the only thing that makes it presentable.

    VRG-388: post-takeover BOTH the slug and the demo enrichment can be absent —
    there is no `-demo` slug off a demo host. `company?.name` is what now covers
    that case, but it is still not guaranteed, so every string in this component
    keeps a brand-less form.
  */
  const apiName =
    brandName?.trim() ||
    data?.demo?.brandName?.trim() ||
    company?.demo?.brandName?.trim() ||
    company?.name?.trim() ||
    null
  const brandLabel =
    apiName ||
    (slug ? slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null)

  // VRG-388 — cold outbound vs a brand that has taken the demo over. See `audience` above.
  const isProspect = audience === 'prospect'

  const label = bannerLabel(brandLabel, audience)

  return (
    <>
      <Box
        component='button'
        type='button'
        onClick={() => setOpen(true)}
        aria-label={`${label}. What does this mean?`}
        sx={{
          /*
            THE WHOLE BAR IS THE TARGET, the chevron only the signal. A chevron
            you must hit precisely is a worse control than a bar you can tap
            anywhere — especially here, where the surface is reached by tapping a
            tag with a thumb.
          */
          /*
            STICKY — the disclosure is PERMANENTLY visible, not merely initially
            visible.

            VRG-313 AC8 makes this bar non-dismissible and VRG-305 Gap 1 makes the
            non-affiliation claim always-visible; a bar that scrolls off the top
            satisfies both only for the first screen, on a page that is several
            screens long. Sticky closes that gap, so treat it as part of the legal
            posture rather than as polish.

            `top` comes from the CALL SITE (`stickyTop`) because what is pinned
            above this bar is page-dependent — see that prop.

            ── Why sticky and not fixed ────────────────────────────────────────
            Sticky keeps the bar IN FLOW, so it still occupies its own height in
            the record column and nothing below it has to be re-offset. `fixed`
            would need the column to reserve the height by hand, and it would break
            on this page anyway for the reason `NavigationHeader` records: the
            page's slide transition leaves a `transform` behind (`fill: both`),
            which reparents a fixed child's containing block and does nothing to a
            sticky one.

            ── The ancestor that looks like it should break this, and does not ──
            `position: sticky` is silently defeated by any ancestor that scrolls,
            and the `<main>` above this bar carries `overflow-x: clip`. That is the
            ONE overflow value that is safe here: `clip` does not create a scroll
            container, where `hidden` does (and would also blockify the
            `overflow-y: visible` beside it into `auto`). Verified in the browser
            rather than reasoned about — the bar pins.

            ── Stacking ────────────────────────────────────────────────────────
            3 is chosen against what is actually on this page, not as a large
            arbitrary number. It must beat the page's own layered elements —
            `AuthenticChip`'s wrapper at `z-index: 1` (which the photograph carries
            past this bar as you scroll) and the sticky bottom action bar at 2 —
            and it must LOSE to `NavigationHeader` at 1100 and to every MUI drawer,
            modal and swipe area at 1199+. A disclosure floating over the drawer
            that explains it, or over any sheet, would be worse than one that
            scrolls away.
          */
          position: 'sticky',
          top: `${stickyTop}px`,
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          // Space-between rather than a gap: the chevron belongs at the TRAILING
          // EDGE of the bar, not trailing the text, so its position is stable
          // whether the sentence takes one line or two.
          justifyContent: 'space-between',
          gap: '12px',
          width: '100%',
          // FULL-BLEED: no horizontal margin, so it reads as a band across the
          // page rather than a card floating in the record column. Pixels, not
          // spacing factors — `apps/app` never overrode MUI's 8px base while
          // `@repo/ui` uses 4px, so a factor copied between the two silently
          // doubles (§29 / settings/tokens.ts).
          padding: '14px 16px',
          borderRadius: 0,
          /*
            `!important` INSIDE the shorthand is mandatory, not defensive:
            `#vera-shadow-root * { border-color: … }` in index.css has ID
            specificity, so an emotion-authored border colour is computed and
            then discarded. See settings/tokens.ts, which documents the same trap
            and the two forms that do and do not survive it.

            Top AND bottom: the photograph above supplies an edge on most pages,
            but at md+ this sits at the top of the record column beside the image
            rather than under it, and a brand may set `bg_color` to pure white —
            either way an unbordered white bar would have no edge at all.
          */
          borderTop: `1px solid ${VERA_HAIRLINE} !important`,
          borderBottom: `1px solid ${VERA_HAIRLINE} !important`,
          borderLeft: 'none !important',
          borderRight: 'none !important',
          backgroundColor: VERA_SURFACE,
          cursor: 'pointer',
          /*
            `font: 'inherit'` is the BUTTON RESET and is kept — without it a
            <button> takes the UA's 13.33px Arial, which is neither the page's
            metrics nor ours. The `fontFamily` longhand AFTER it keeps the whole
            reset (style, weight, size, line-height) and replaces only the family,
            which is the half that must not come from the tenant. See
            VERA_TYPEFACE.
          */
          font: 'inherit',
          fontFamily: VERA_TYPEFACE,
          textAlign: 'left',
          // The chevron travels on hover, and ONLY the chevron — the same choice
          // `AuthenticChip` documents: the travel is the message, a fill shift is
          // noise. Gated on `hover: hover` because on touch `:hover` STICKS after a
          // tap, which reads as a stuck control.
          '@media (hover: hover)': {
            '&:hover': {
              '& [data-demo-chevron]': { transform: 'translateX(3px)' },
            },
          },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `inset 0 0 0 3px ${VERA_FOCUS_RING}`,
            '& [data-demo-chevron]': { transform: 'translateX(2px)' },
          },
        }}
      >
        <Typography
          sx={{
            // Explicit size, not a variant: `caption`/`body2` have both moved in
            // this app's scale, and 14 is the documented readable floor on the
            // phone this page is reached from (settings/tokens.ts `font`).
            fontSize: `${font.label}px`,
            lineHeight: 1.4,
            fontWeight: 600,
            color: VERA_INK,
            // Vera's face, not the tenant's — see VERA_TYPEFACE. Restated here
            // because `Typography` declares its own `font-family` from the theme
            // and would beat the bar's inherited one.
            fontFamily: VERA_TYPEFACE,
            /*
              DELIBERATELY NOT `whiteSpace: 'nowrap'`.

              This sentence's rendered width is a function of the TENANT'S font,
              which is unbounded — measured at 375 on the earlier, SHORTER chip
              copy it ran 254px in Lato, 273px in the system stack and 321px in
              Space Mono. `nowrap` would clip the disclosure on a brand whose
              Studio font is merely wider than average, and clipping is the one
              failure this element cannot have: the truncated half is the legally
              load-bearing one.

              A bar can absorb a second line where a pill could not, so wrapping
              is close to free here. `minWidth: 0` is what actually permits it — a
              flex item defaults to `min-width: auto` (sized to its content) and
              would push the row wider rather than wrap.

              ⚠️ WRAPPING IS ALSO THE ANSWER TO A LONG BRAND NAME, and it is the
              only acceptable one. Naming the brand made this sentence's length
              unbounded in a second, worse way: the tenant's font was already
              unbounded, and now so is the subject. Measured at 375: "Allbirds"
              fits one line, while a name like "Maison Margiela Paris" takes two.

              TRUNCATION WAS REJECTED, not merely not-chosen. `text-overflow`
              cuts the END of the string, and the end is where BOTH the brand's
              name and the second half of the claim live — so the failure mode is
              a disclosure reading "…not issued by Long Brand Nam…", which is a
              legal claim about an unidentified party. A two-line bar costs ~19px
              of a page that scrolls anyway. There is no width at which clipping
              this sentence is the better trade.
            */
            minWidth: 0,
          }}
        >
          {label}
        </Typography>
        {/* Visible tap affordance, at the trailing edge. Without it the drawer is
            reachable only by guessing — the same gap `AuthenticChip` closed when
            its explanation became the only route to what it meant.

            Black, at the bar's own ink, rather than a demoted grey: on a
            two-element row it is one of only two marks present, and a faded
            chevron on a white bar with nothing else in it reads as disabled. */}
        <Box
          component={ChevronRight}
          aria-hidden
          data-demo-chevron
          sx={{
            width: 18,
            height: 18,
            flexShrink: 0,
            color: VERA_INK,
            transition: 'transform 160ms ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
          strokeWidth={1.75}
        />
      </Box>

      <DrawerPopup
        title='Demo certificate'
        handleOpen={open}
        handleClose={() => setOpen(false)}
        child={
          <Stack
            spacing={3}
            sx={{
              pb: 1,
              /*
                THE DRAWER IS THE SAME VOICE AT GREATER LENGTH, so it takes the
                same face as the bar. The full non-affiliation sentence lives here;
                pinning the one-line claim and letting its explanation render in
                the brand's typeface would be incoherent, and it is the longer text
                where a serif display face does the most damage to legibility.

                One rule rather than a `fontFamily` on each child: `Typography` and
                `Button` both write the theme's family explicitly, so the selector
                has to name them — an inherited value on this Stack loses.

                ⚠️ The sheet's own CHROME (title, handle, close) is deliberately NOT
                covered — that is `DrawerPopup`'s shared furniture, and it is the
                same line already drawn for COLOUR a few lines below, where body ink
                is pinned to Vera's and the chrome is left alone.
              */
              '& .MuiTypography-root, & .MuiButton-root': {
                fontFamily: VERA_TYPEFACE,
              },
            }}
          >
            {/* Body ink is pinned to Vera's for the reason in VERA_SURFACE's block:
                these are the sentences carrying the legal claim, and a tenant's
                `text_color` must not be able to wash them out. The sheet's own
                chrome (title, handle, close) stays `DrawerPopup`'s — it is shared
                furniture, not this disclosure's content. */}
            <Typography sx={{ fontSize: 16, lineHeight: 1.5, color: VERA_INK }}>
              {isProspect
                ? brandLabel
                  ? `This certificate was created by Vera to show what ${brandLabel}'s product certificates could look like.`
                  : 'This certificate was created by Vera to show what this brand’s product certificates could look like.'
                : // POST-TAKEOVER. States what the RECORD is. No "× Vera" headline:
                  // on a customer's own live domain that reads to their visitors as a
                  // partnership announcement, which is a different claim from the one
                  // being made here.
                  'This is a demo record, not a real item.'}
            </Typography>

            <Typography sx={{ fontSize: 16, lineHeight: 1.5, color: VERA_INK_MUTED }}>
              {isProspect
                ? // The non-affiliation sentence, verbatim from the reviewed copy. It
                  // is the claim "Demo" does not make, and the reason this drawer
                  // exists rather than the bar standing alone.
                  brandLabel
                  ? `It was not issued by ${brandLabel}, and Vera is not affiliated with them. The product shown was taken from their public website.`
                  : 'It was not issued by the brand, and Vera is not affiliated with them. The product shown was taken from their public website.'
                : // The non-affiliation sentence is DELETED rather than softened,
                  // because by this point it is simply untrue. What survives is the
                  // part that still protects the visitor: this record is not a real
                  // item, and its content was not authored by the brand whose site
                  // they are on. Plain sentences, no em-dash chain (§15).
                  brandLabel
                  ? `It was created by Vera to show how certificates work, so its images and details were not provided by ${brandLabel}.`
                  : 'It was created by Vera to show how certificates work, so its images and details were not provided by the brand.'}
            </Typography>

            {/* Epic AC3: a COLD recipient must have a next action.
                VRG-388 — and only a cold one. Post-takeover this button sits on a paying
                customer's own live product page and sends THEIR visitors to Vera's marketing
                site. That is not a disclosure, it is us advertising off their traffic, and it
                is the same category of mistake as the non-affiliation line: correct for the
                audience it was written for, wrong for the one now reading it. The disclosure
                above is what has to survive takeover; the pitch is what has to stop. */}
            {isProspect && (
              <Button
                variant='primary'
                href={ctaUrl}
                target='_blank'
                rel='noopener noreferrer'
                endIcon={<ArrowUpRight size={16} strokeWidth={1.75} />}
                // Vera's ink, for the same reason the bar is: a CTA filled with the
                // tenant's accent would read as the brand endorsing our pitch.
                sx={{
                  width: '100%',
                  backgroundColor: VERA_INK,
                  color: VERA_SURFACE,
                  '&:hover': { backgroundColor: '#3F3F46' },
                }}
              >
                See what Vera does
              </Button>
            )}
          </Stack>
        }
      />
    </>
  )
}

export default DemoBanner
